import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { CircularProgress, Stack } from '@mui/material'
import { Box } from '@mui/system'
import React, { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useUpdateInstallation } from 'src/Libs/installation-details/hooks'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCreateNewInstallationFormInitializer } from 'src/Libs/installations/hooks/installation-configuration/use-create-new-installation-form-initializer'
import { Installation, NewInstallationFormModel } from '../../../../models/installation.model'
import NewInstallationForm from '../../NewInstallationForm'
import SaveConfigurationButton from './SaveConfigurationButton'

type BasicConfigurationProps = {
  installation?: Installation
  installationId?: number
  installationLoading: boolean
}

const BasicConfiguration: React.FC<BasicConfigurationProps> = ({ installation, installationId, installationLoading }) => {
  const { t } = useCustomTranslation()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const { initialValues, validationSchema } = useCreateNewInstallationFormInitializer(installation)

  const methods = useForm<NewInstallationFormModel>({
    defaultValues: initialValues,
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
  })

  useEffect(() => {
    if (installationLoading || !installation) return

    methods.reset(initialValues)
  }, [installationId, installation, installation?.partnersInstallationName, installationLoading, initialValues, methods])

  const { mutate: updateInstallation } = useUpdateInstallation({
    onSuccess: () => {
      enqueueSuccessStack(t('installation-configuration.general-settings.save.success'))
    },
    onError: error => {
      enqueueErrorStack(t('installation-configuration.general-settings.save.error'))
      console.error('error saving installation configuration general settings', error)
    },
  })

  const onSubmit = (data: NewInstallationFormModel): void => {
    if (!installationId) return

    const { storageType, ...payload } = data
    updateInstallation({ installationId, payload })
  }

  return (
    <AccordionItem title={t('installation-configuration.general-settings.title')} expanded accordionDetailsSx={{ px: 4, py: 2 }}>
      <Stack justifyContent={'center'} alignItems={'center'}>
        {installationLoading ? (
          <CircularProgress />
        ) : (
          <FormProvider {...methods}>
            <Box sx={{ width: '100%' }}>
              <form onSubmit={methods.handleSubmit(onSubmit)}>
                <NewInstallationForm />
                <SaveConfigurationButton />
              </form>
            </Box>
          </FormProvider>
        )}
      </Stack>
    </AccordionItem>
  )
}

export default BasicConfiguration
